/**
 * bubble-form-sync.js
 * ---------------------------------------------------------------
 * Sends a copy of "wholesale enquiry" contact form submissions to
 * Bubble.io API Workflow(s), WITHOUT changing Shopify's native
 * contact form behaviour in any way. The form still submits to
 * Shopify normally, the store's contact-form email notification
 * still goes out, and the built-in success/error page still shows —
 * this script just quietly fires extra copies of the data to Bubble
 * in parallel.
 *
 * It only acts on the specific form that contains the hidden field
 *   <input name="contact[Enquiry]" value="Wholesale">
 * so it will not touch any other contact forms on the site (e.g. a
 * general "Contact Us" page), even if they share the same CSS class.
 *
 * DUAL LIVE + TEST SENDING
 * While you're testing, this posts the same submission to BOTH your
 * LIVE and TEST Bubble apps at the same time, independently — if one
 * fails, it does not affect or block the other. Once you've
 * confirmed LIVE is working correctly, stop sending to TEST by
 * commenting out a single line — see "postToBubble(BUBBLE_TEST, ...)"
 * near the bottom of this file, inside sendToBubble().
 *
 * SETUP — 3 steps:
 *  1. Fill in the BUBBLE_LIVE and BUBBLE_TEST config blocks below
 *     with your real endpoint URLs and tokens.
 *  2. In Shopify Admin: Online Store > Themes > Edit code > Assets
 *     > "Add a new asset" > Create a blank file named exactly
 *     "bubble-form-sync.js" and paste this whole file into it.
 *  3. In the section file that renders this form (the one with
 *     {% form 'contact', id: form_id, ... %}), add this single line
 *     immediately after the {%- endform -%} line:
 *
 *       {{ 'bubble-form-sync.js' | asset_url | script_tag }}
 *
 * IMPORTANT — before this will actually deliver data correctly:
 *  - The payload sends: name, email, phone, business_name,
 *    business_location, volume, status, comment, star_sign. Double
 *    check these match your Bubble workflow's parameter names
 *    EXACTLY (Bubble parameter names are case-sensitive), on BOTH
 *    the live and test versions of the workflow.
 *  - status is sent as "Currently Open" or "Under construction"
 *    (mapped from the dropdown's underlying open/closed value) —
 *    make sure that matches the option text/choice set in Bubble.
 *  - endpointUrl below must NOT have a trailing path segment beyond
 *    your workflow's own name (e.g. no extra "/initialize" at the
 *    end) — it should end in the workflow name itself, matching
 *    what's shown at the top of that workflow's page in the Bubble
 *    editor. Edit the two endpointUrl values below with your real,
 *    corrected URLs.
 *  - Bubble's TEST (development) environment URL is normally the
 *    same as LIVE but with "/version-test" inserted before
 *    "/api/1.1/wf/...". Double check yours matches what Bubble shows
 *    you rather than assuming.
 *  - In Bubble, each workflow (live and test versions) must have
 *    "Expose as public API workflow" turned on.
 *  - Because this code runs in the customer's browser, anyone who
 *    views page source can see both tokens below. Use a token
 *    generated specifically for this integration on each
 *    environment (see prior discussion on limiting blast radius) —
 *    Bubble doesn't support scoping a token to one workflow, so keep
 *    what each exposed workflow can do to a minimum instead.
 * ---------------------------------------------------------------
 */
(function () {
  "use strict";

  // ---------------- CONFIGURE THESE VALUES ----------------
  var BUBBLE_LIVE = {
    label: "LIVE",
    endpointUrl: "https://geppetto-30616.bubbleapps.io/version-live/api/1.1/wf/shopify-lead-submission",
    apiToken: "e25aa1da0a7ec7c592f8fbb58ef0ef21",
  };

  var BUBBLE_TEST = {
    label: "TEST",
    endpointUrl:
      "https://geppetto-30616.bubbleapps.io/version-test/api/1.1/wf/shopify-lead-submission",
    apiToken: "e25aa1da0a7ec7c592f8fbb58ef0ef21",
  };
  // ----------------------------------------------------------

  // Only this exact contact form (identified by its hidden Enquiry
  // field) gets synced to Bubble — every other contact form on the
  // site is left completely alone.
  function isWholesaleForm(form) {
    var enquiryField = form.querySelector('input[name="contact[Enquiry]"]');
    return !!enquiryField && enquiryField.value === "Wholesale";
  }

  // The <select> stores "open"/"closed" as its value, but Bubble
  // expects the human-readable label shown to the customer.
  var STATUS_LABELS = {
    open: "Currently Open",
    closed: "Under construction",
  };

  function buildPayload(form) {
    var data = new FormData(form);
    var rawStatus = data.get("contact[status]") || "";

    return {
      name: data.get("contact[name]") || "",
      email: data.get("contact[email]") || "",
      phone: data.get("contact[phone]") || "",
      business_name: data.get("contact[businessname]") || "",
      business_location: data.get("contact[businesslocation]") || "",
      volume: data.get("contact[volume]") || "",
      status: STATUS_LABELS[rawStatus] || rawStatus,
      comment: data.get("contact[body]") || "",
      star_sign: data.get("contact[starsign]") || "",
    };
  }

  // Posts to a single Bubble target (LIVE or TEST). Each call is
  // fully independent — a failure or slow response from one target
  // never affects, delays, or cancels the other.
  function postToBubble(target, payload) {
    try {
      fetch(target.endpointUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": "Bearer " + target.apiToken,
        },
        body: JSON.stringify(payload),
        // keepalive lets this request keep running even though the
        // browser starts navigating away almost immediately (because
        // Shopify's own native form submission is not being
        // prevented). Without this, the request can be cancelled
        // mid-flight before Bubble ever receives it.
        keepalive: true,
      }).catch(function (err) {
        // A Bubble failure must never be visible to the customer or
        // block/slow down the real Shopify submission.
        if (window.console && console.warn) {
          console.warn("Bubble sync (" + target.label + ") failed:", err);
        }
      });
    } catch (err) {
      if (window.console && console.warn) {
        console.warn("Bubble sync (" + target.label + ") error:", err);
      }
    }
  }

  function sendToBubble(form) {
    var payload = buildPayload(form);

    // postToBubble(BUBBLE_LIVE, payload);

    // Once you've confirmed submissions are landing correctly in
    // your LIVE Bubble app, comment out the line below to stop
    // sending test-environment traffic:
    postToBubble(BUBBLE_TEST, payload);
  }

  // Capture phase (the "true" third argument) so we catch the
  // submit event as early as possible, before Shopify's own
  // handling begins navigating the page away.
  document.addEventListener(
    "submit",
    function (event) {
      var form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (!form.classList.contains("contact-form__form")) return;
      if (!isWholesaleForm(form)) return;

      // Deliberately NOT calling event.preventDefault() here.
      // Shopify's native submission — email notification, spam
      // protection, success/error page — continues exactly as it
      // does today. This is purely additive.
      sendToBubble(form);
    },
    true
  );
})();